import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChevronDown, Loader2, TrendingDown, TrendingUp, X } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { salesCountLabel, salesUnitFor } from '../../lib/salesUnits';
import {
  useSalesMonthDetails,
  type SalesDayRow,
  type SalesShareRow,
} from '../../hooks/useSalesMonthDetails';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const STACKS = [
  { key: 'bookings', label: 'Bookings', color: '#2563eb' },
  { key: 'invoices', label: 'Counter sales', color: '#8b5cf6' },
] as const;

interface SalesMonthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function trendPct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function TrendChip({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[12px] font-semibold tabular-nums ${
        up ? 'text-green-700' : 'text-red-700'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </h3>
  );
}

function StatBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className="text-sm font-semibold tabular-nums tracking-tight text-gray-900">{value}</p>
    </div>
  );
}

function DailySalesChart({ days }: { days: SalesDayRow[] }) {
  const activeStacks = STACKS.filter(({ key }) => days.some((d) => d[key] > 0));
  const chartData: ChartData<'bar', number[], string> = {
    labels: days.map((d) => d.label),
    datasets: activeStacks.map(({ key, label, color }) => ({
      label,
      data: days.map((d) => d[key]),
      backgroundColor: color,
      borderRadius: 3,
      maxBarThickness: 16,
    })),
  };

  return (
    <div className="h-[200px] w-full">
      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const day = days[items[0]?.dataIndex ?? -1];
                  return day ? `${day.label} — ${formatCurrency(day.total)}` : '';
                },
                label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw as number)}`,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12, font: { size: 10 } },
            },
            y: {
              stacked: true,
              grid: { color: 'rgba(0,0,0,0.06)' },
              ticks: { font: { size: 10 } },
            },
          },
        }}
      />
    </div>
  );
}

function ShareRows({
  rows,
  barColor,
  countNoun,
  emptyCopy,
}: {
  rows: SalesShareRow[];
  barColor: string | ((row: SalesShareRow) => string);
  countNoun: string;
  emptyCopy: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-6">{emptyCopy}</p>;
  }
  return (
    <div className="divide-y divide-gray-100">
      {rows.map((row) => {
        const color = typeof barColor === 'function' ? barColor(row) : barColor;
        return (
          <div key={row.key} className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex items-baseline gap-1.5 text-sm font-medium text-gray-800 min-w-0">
                <span
                  className="h-2 w-2 self-center rounded-full shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span className="truncate">{row.label}</span>
                <span className="text-[11px] font-normal text-gray-400 shrink-0">
                  {row.count} {countNoun}
                  {row.count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                {formatCurrency(row.amount)}
                <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                  {(row.share * 100).toFixed(0)}%
                </span>
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.max(row.share * 100, 1.5)}%`, backgroundColor: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Full sales picture for the month — the deep-dive behind the Sales tile. */
export default function SalesMonthModal({ isOpen, onClose }: SalesMonthModalProps) {
  const {
    summary,
    days,
    bookedDays,
    sources,
    topServices,
    feedDays,
    monthLabel,
    loading,
    error,
    load,
  } = useSalesMonthDetails();
  const [feedExpanded, setFeedExpanded] = useState(false);
  const [chartMode, setChartMode] = useState<'delivered' | 'booked'>('delivered');

  useEffect(() => {
    if (isOpen) {
      setFeedExpanded(false);
      setChartMode('delivered');
      void load();
    }
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const pct = trendPct(summary.totalSales, summary.lastMonthTotal);
  const whoRows: SalesShareRow[] = [
    {
      key: 'new',
      label: 'New customers',
      amount: summary.newRevenue,
      count: summary.newCount,
      share: summary.bookingSales > 0 ? summary.newRevenue / summary.bookingSales : 0,
    },
    {
      key: 'repeat',
      label: 'Repeat customers',
      amount: summary.repeatRevenue,
      count: summary.repeatCount,
      share: summary.bookingSales > 0 ? summary.repeatRevenue / summary.bookingSales : 0,
    },
  ].filter((row) => row.count > 0);

  const salesCount = summary.bookingCount + summary.invoiceCount;
  const salesUnit = salesUnitFor(summary.bookingCount, summary.invoiceCount);

  return createPortal(
    <div
      className="fixed inset-0 z-[99998] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sales-month-title"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="relative w-full max-w-3xl max-h-[min(92vh,760px)] flex flex-col rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 id="sales-month-title" className="text-base font-semibold text-gray-900">
              Sales this month
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{monthLabel || 'Month to date'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Headline strip */}
        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 shrink-0">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
          ) : (
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Sales this month
                </p>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <p className="text-2xl font-bold text-gray-900 tabular-nums tracking-tight">
                    {formatCurrency(summary.totalSales)}
                  </p>
                  <TrendChip pct={pct} />
                </div>
                {pct !== null ? (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    vs {formatCurrency(summary.lastMonthTotal)} last month
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-start gap-x-5 gap-y-2 sm:gap-x-7">
                <StatBlock label="Today" value={formatCurrency(summary.salesToday)} />
                <StatBlock label="Booked today" value={formatCurrency(summary.bookedToday)} />
                <StatBlock label="Avg per day" value={formatCurrency(summary.avgPerDay)} />
                <StatBlock
                  label="Projected month-end"
                  value={formatCurrency(summary.projectedMonthEnd)}
                />
                <StatBlock label={salesCountLabel(salesUnit)} value={String(salesCount)} />
                <StatBlock label="Avg sale" value={formatCurrency(summary.avgSale)} />
              </div>
            </div>
          )}
        </div>

        {/* Scroll body */}
        <div className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-4 py-4 space-y-5">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 text-center py-10">{error}</p>
          ) : (
            <>
              {/* Daily sales chart — toggle between delivery and booked axes */}
              {(() => {
                const chartDays = chartMode === 'booked' ? bookedDays : days;
                return (
                  <section>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                      <div className="flex items-center gap-2.5">
                        <SectionTitle>Daily sales</SectionTitle>
                        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                          {(
                            [
                              ['delivered', 'Delivered'],
                              ['booked', 'Booked'],
                            ] as const
                          ).map(([mode, label]) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setChartMode(mode)}
                              className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                                chartMode === mode
                                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                        {STACKS.filter(({ key }) => chartDays.some((d) => d[key] > 0)).map(
                          ({ label, color }) => (
                            <span key={label} className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                              {label}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-400">
                      {chartMode === 'booked'
                        ? 'By the day the sale was created.'
                        : 'By the day the job finishes (matches the monthly total).'}
                    </p>
                    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3">
                      {chartDays.every((d) => d.total === 0) ? (
                        <p className="text-sm text-gray-500 text-center py-8">
                          No sales recorded this month.
                        </p>
                      ) : (
                        <DailySalesChart days={chartDays} />
                      )}
                    </div>
                  </section>
                );
              })()}

              {/* Sales by source */}
              <section>
                <SectionTitle>Where it came from</SectionTitle>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <ShareRows
                    rows={sources}
                    barColor={(row) => (row.key === '__invoices' ? '#8b5cf6' : '#2563eb')}
                    countNoun="sale"
                    emptyCopy="No sales yet."
                  />
                </div>
              </section>

              {/* New vs repeat + top services */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <section>
                  <SectionTitle>Who's buying</SectionTitle>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <ShareRows
                      rows={whoRows}
                      barColor={(row) => (row.key === 'new' ? '#10b981' : '#2563eb')}
                      countNoun="job"
                      emptyCopy="No bookings yet."
                    />
                  </div>
                </section>

                <section>
                  <SectionTitle>Top services</SectionTitle>
                  <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <ShareRows
                      rows={topServices}
                      barColor="#f59e0b"
                      countNoun="job"
                      emptyCopy="No services sold yet."
                    />
                  </div>
                </section>
              </div>

              {/* Composition */}
              <section>
                <SectionTitle>Inside the number</SectionTitle>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Booking subtotals</span>
                    <span className="font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(summary.grossSubtotal)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Discounts</span>
                    <span
                      className={`font-semibold tabular-nums ${
                        summary.discounts > 0 ? 'text-red-700' : 'text-gray-900'
                      }`}
                    >
                      {summary.discounts > 0
                        ? `−${formatCurrency(summary.discounts)}`
                        : formatCurrency(0)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                    <span className="text-gray-600">Booking sales</span>
                    <span className="font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(summary.bookingSales)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-gray-600">Counter sales & invoices</span>
                    <span className="font-semibold text-gray-900 tabular-nums">
                      + {formatCurrency(summary.invoiceSales)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-3 py-1.5 border-t border-gray-100">
                    <span className="font-medium text-gray-800">Sales this month</span>
                    <span className="font-bold text-gray-900 tabular-nums">
                      {formatCurrency(summary.totalSales)}
                    </span>
                  </div>
                  <p className="pt-1.5 text-[11px] text-gray-400">
                    Pre-tax. Jobs count toward the month they finish in, so scheduled work
                    ending later this month is included.
                  </p>
                </div>
              </section>

              {/* Sales feed — collapsed by default */}
              <section>
                <div className="mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFeedExpanded((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                    aria-expanded={feedExpanded}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Sales this month
                      {salesCount > 0 ? ` · ${salesCount}` : ''}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        feedExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {!feedExpanded ? null : feedDays.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6 border-t border-gray-100">
                      No sales recorded this month.
                    </p>
                  ) : (
                    <div className="border-t border-gray-100">
                      {feedDays.map((day) => (
                        <div key={day.date}>
                          <div
                            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide border-b border-gray-100 ${
                              day.isToday
                                ? 'bg-blue-50/80 text-blue-700'
                                : 'bg-gray-50/80 text-gray-500'
                            }`}
                          >
                            {day.label}
                            {day.isToday ? ' · Today' : ''}
                          </div>
                          <ul className="divide-y divide-gray-50">
                            {day.rows.map((row) => (
                              <li
                                key={row.id}
                                className="flex items-center justify-between gap-3 px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">
                                    {row.name}
                                    {row.isNew === true ? (
                                      <span className="ml-1.5 text-[10px] font-semibold uppercase text-green-700">
                                        New
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className="text-[11px] text-gray-500">{row.detail}</p>
                                </div>
                                <p className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                                  {formatCurrency(row.amount)}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
